// prop-types is library for typechecking of props
import PropTypes from "prop-types";
import { useEffect, useState } from "react";

// @mui material components
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import DraftsOutlinedIcon from "@mui/icons-material/DraftsOutlined";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import NotificationImportantOutlinedIcon from "@mui/icons-material/NotificationImportantOutlined";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import flowThumbnail from "assets/images/for-flow/default-flow-icon.svg";
import SoftAvatar from "components/SoftAvatar";
import SoftBadge from "components/SoftBadge";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import FlowMenu from "layouts/Flows/components/FlowMenu";
import DefaultUserAvatar from "layouts/pages/account/components/Header/components/DefaultUserAvatar";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import StatusBadge from "cxfcomponents/StatusBadge";
import useThemeDetector from "hooks/useThemeDetector";
import { getEnv } from "../../../../helpers/environment";

function ComplexProjectCard({
  title,
  dateTime = "",
  description,
  members = [],
  actionElement,
  dropdown = false,
  status,
  publishStatus,
  actionForRF,
  item,
  flows,
  flowUserPermissions,
  setOpenUsersModal,
  tenant_id,
  flow_id,
  schedule_id,
  role_id,
  getFlows,
  setSelectedUserFlow,
  icon,
  sharedStatus,
  onFlowClick,
}) {
  const [imageError, setImageError] = useState(false);
  const handleImageError = () => {
    setImageError(true);
  };
  const [shouldExpand, setShouldExpand] = useState(false);
  const navigate = useNavigate();
  const { isDarkMode } = useThemeDetector();
  const getMemberLabel = (member) => {
    const fullName = `${member?.first_name ?? ""} ${member?.last_name ?? ""}`.trim();
    return fullName || member?.email_address || member?.email || "";
  };
  const uniqueMembers = members.reduce((acc, member) => {
    const memberKey =
      member?.auth0_id || member?.id || member?.email_address || member?.email || member?.image;

    if (!acc.some((item) => (item?.auth0_id || item?.id || item?.email_address || item?.email || item?.image) === memberKey)) {
      acc.push(member);
    }

    return acc;
  }, []);

  const renderMembers = uniqueMembers.map((member, key) => {
    const memberKey =
      member?.auth0_id || member?.id || member?.email_address || member?.email || `member-${key}`;
    const tooltipTitle = getMemberLabel(member);

    return (
      <SoftBox
        key={memberKey}
        sx={{
          ml: key === 0 ? 0 : -1.25,
          display: "flex",
          alignItems: "center",
          position: "relative",
          "&:hover, &:focus-within": {
            zIndex: 10
          }
        }}
      >
        {member?.image && member.image.trim() ? (
          <Tooltip title={tooltipTitle} placement="bottom">
            <SoftAvatar
              src={member?.image}
              alt={tooltipTitle}
              size="xs"
              sx={({ borders: { borderWidth }, palette: { white } }) => ({
                border: `${borderWidth[2]} solid ${white.main}`,
                cursor: "pointer"
              })}
            />
          </Tooltip>
        ) : (
          <DefaultUserAvatar
            firstName={member?.first_name}
            lastName={member?.last_name}
            email={member?.email_address || member?.email}
            size="xs"
            cursor="pointer"
            placement="bottom"
            sx={({ borders: { borderWidth }, palette: { white } }) => ({
              border: `${borderWidth[2]} solid ${white.main}`,
              cursor: "pointer"
            })}
          />
        )}
      </SoftBox>
    );
  });

  const statusBadge = (status, color) => {
    return (
      <SoftBadge variant="contained" size="xs" color={color} container badgeContent={status}>
        <SoftBox mr={1}>
          {status === "deleted" ? (
            <DeleteForeverOutlinedIcon color={color} />
          ) : status === "draft" ? (
            <DraftsOutlinedIcon color={color} />
          ) : status === "inactive" ? (
            <NotificationImportantOutlinedIcon color={color} />
          ) : (
            <ModeEditOutlineOutlinedIcon color={color} />
          )}
        </SoftBox>
      </SoftBadge>
    );
  };

  const renderPublishStatus = (value) => {
    switch (value) {
      case "For Approval":
        return statusBadge(value, "warning");
      case "Declined":
        return statusBadge(value, "error");
      case "Published":
        return statusBadge(value, "success");
      default:
        return null;
    }
  };

  useEffect(() => {
    if (description?.length > 56) {
      setShouldExpand(false);
    }
  }, [description]);

  const renderDescription = () => {
    if (!shouldExpand && description?.length > 48) {
      return (
        <>
          {description?.slice(0, 48)}...
          <SoftBox
            as="span"
            display="inline-block"
            fontWeight="bold"
            fontSize="14px"
            onClick={() => setShouldExpand(true)}
            sx={{ textDecoration: "underline", cursor: "pointer", marginLeft: "4px" }}
          >
            Show more
          </SoftBox>
        </>
      );
    }

    return (
      <>
        {description}
        {description?.length > 48 && (
          <SoftBox
            as="span"
            display="inline-block"
            fontWeight="bold"
            fontSize="14px"
            onClick={() => setShouldExpand(false)}
            sx={{ textDecoration: "underline", cursor: "pointer", marginLeft: "4px" }}
          >
            Show less
          </SoftBox>
        )}
      </>
    );
  };

  if (status === "deleted") {
    return (
      <Card
        sx={{
          height: "100%",
          boxShadow: isDarkMode ? "0px 20px 27px rgba(0, 0, 0, 0.05)" : "none"
        }}
      >
        <SoftBox
          p={3}
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          height="100%"
        >
          <SoftBox display="flex">
            <SoftAvatar
              src={
                imageError
                  ? flowThumbnail
                  : `${getEnv("S3_PUBLIC_URL")}/${getEnv(
                      "KEY_ROUTE"
                    )}/flowThumbnail/${tenant_id}/${flow_id}.png?${new Date().getTime()}`
              }
              onError={handleImageError}
              alt={title}
              size="xl"
              variant="rounded"
              sx={{
                height: {
                  sm: "64px",
                  lg: "92px"
                },
                width: {
                  sm: "64px",
                  lg: "92px"
                },
                "& img": {
                  height: "100%"
                },
              }}
            />
            <SoftBox ml={3} lineHeight={0} marginTop="8px">
              <SoftBox mb={1} lineHeight={0}>
                <SoftTypography
                  className="white-to-dark"
                  variant="h6"
                  textTransform="capitalize"
                  fontWeight="bold"
                  fontSize="14px"
                  sx={{ color: "#1E283E", cursor: "pointer" }}
                  onClick={() => navigate(`/flow/${flow_id}`)}
                >
                  {title?.length >= 32 ? title?.slice(0, 32) + "..." : title}
                </SoftTypography>
              </SoftBox>
              <SoftBox display="flex" gap={1}>
                <StatusBadge status={status === "active" ? "customActive" : status} />
                {publishStatus && renderPublishStatus(publishStatus)}
              </SoftBox>
            </SoftBox>
            {actionElement && (
              <SoftTypography
                color="secondary"
                onClick={dropdown?.action}
                sx={{
                  ml: "auto",
                  alignSelf: "flex-start",
                  py: 1.25,
                  transform: "rotate(90deg)"
                }}
              >
                {actionElement}
              </SoftTypography>
            )}
            {dropdown && (
              <SoftTypography
                color="secondary"
                onClick={dropdown?.action}
                sx={{
                  ml: "auto",
                  alignSelf: "flex-start",
                  py: 1.25
                }}
              >
                <Icon fontSize="default" sx={{ cursor: "pointer" }}>
                  more_horiz
                </Icon>
              </SoftTypography>
            )}
            {dropdown?.menu}
            {actionForRF && (
              <SoftTypography
                color="secondary"
                sx={{
                  ml: "auto",
                  alignSelf: "flex-start",
                  py: 1.25
                }}
              >
                <FlowMenu
                  item={item}
                  flows={flows}
                  getFlows={getFlows}
                  flowUserPermissions={flowUserPermissions}
                  setOpenUsersModal={setOpenUsersModal}
                  setSelectedUserFlow={setSelectedUserFlow}
                  actionForRF
                />
              </SoftTypography>
            )}
          </SoftBox>
          {description && (
            <SoftBox marginTop="16px" lineHeight={1}>
              <SoftTypography
                variant="button"
                fontWeight="regular"
                color="text"
                sx={{
                  wordBreak: "break-word",
                  fontSize: {
                    sm: "12px",
                    lg: "14px"
                  }
                }}
              >
                {renderDescription()}
              </SoftTypography>
            </SoftBox>
          )}
          <div>
            <SoftBox
              className="browse-tiles-divider"
              sx={{
                height: "1px",
                backgroundColor: "#E0E1E2",
                marginTop: "24px",
                marginBottom: "20px"
              }}
            />
            <SoftBox display="flex" justifyContent="space-between" alignItems="center">
              {members.length > -1 ? (
                <SoftBox display="flex" alignItems="center">
                  {renderMembers}
                </SoftBox>
              ) : null}
              {dateTime ? (
                <SoftBox display="flex" flexDirection="column" lineHeight={0}>
                  <SoftTypography
                    color="text"
                    sx={{
                      fontSize: "10px",
                    }}
                  >
                    <span style={{ fontWeight: 500, fontSize: "12px" }}>Last Edited:</span>
                    <span
                      style={{ display: "inline-flex", flexDirection: "column", marginLeft: "4px" }}
                    >
                      <span>{moment(dateTime).format("MM/DD/YYYY")}</span>
                      <span style={{ lineHeight: 1 }}>{moment(dateTime).format("h:mm:ss A")}</span>
                    </span>
                  </SoftTypography>
                </SoftBox>
              ) : null}
            </SoftBox>
          </div>
        </SoftBox>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        width: "100%",
        minWidth: "373px",
        height: "100%",
        boxShadow: isDarkMode ? "0px 20px 27px rgba(0, 0, 0, 0.05)" : "none",
        margin: "0 auto"
      }}
    >
      <SoftBox
        p={3}
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        height="100%"
      >
        <SoftBox display="flex">
          <SoftAvatar
            src={
              icon
                ? icon
                : imageError
                ? flowThumbnail
                : `${getEnv("S3_PUBLIC_URL")}/${getEnv(
                    "KEY_ROUTE"
                  )}/flowThumbnail/${tenant_id}/${flow_id}.png?${new Date().getTime()}`
            }
            onError={handleImageError}
            alt={title}
            size="xl"
            variant="rounded"
            sx={{
              height: {
                sm: "64px",
                lg: "94px"
              },
              width: {
                sm: "64px",
                lg: "94px"
              },
              "& img": {
                height: "100%"
              },
              border: role_id ? "none !important" : "0.5px solid #e6e6e6 !important",
              backgroundColor: role_id ? "transparent !important" : undefined,
              borderRadius: role_id ? "20px !important" : undefined,
            }}
          />
          <SoftBox ml={3} marginTop="8px" lineHeight={0}>
            <SoftBox>
              <SoftBox lineHeight={0} marginBottom={sharedStatus ? "0" : "4px"}>
                <SoftTypography
                  className="white-to-dark"
                  variant="h6"
                  textTransform="capitalize"
                  fontWeight="bold"
                  fontSize="14px"
                  sx={{ color: "#1E283E", cursor: "pointer" }}
                  onClick={() => {
                    if (onFlowClick && flow_id) {
                      onFlowClick(flow_id);
                      return;
                    }
                    if (flow_id) {
                      if (window.location.href.includes("designer")) {
                        navigate(`/designer/flow/${flow_id}`, { replace: true });
                        return;
                      }
                      navigate(`/flow/${flow_id}`);
                    }
                    if (schedule_id) {
                      navigate(`/schedule/${schedule_id}`);
                    }
                    if (role_id) {
                      navigate(`/administration/roles-and-permissions/${role_id}`);
                    }
                  }}
                >
                  {title?.length >= 32 ? title?.slice(0, 32) + "..." : title}
                </SoftTypography>
              </SoftBox>
              {!!sharedStatus && (
                <SoftTypography
                  sx={{
                    fontWeight: 500,
                    marginBottom: "2px",
                    fontSize: {
                      sm: "12px",
                      lg: "14px"
                    }
                  }}
                >
                  {sharedStatus}
                </SoftTypography>
              )}
              <SoftBox display="flex" gap={1}>
                <StatusBadge status={status} />
                {publishStatus && renderPublishStatus(publishStatus)}
              </SoftBox>
            </SoftBox>
            {description && (
              <SoftBox marginTop="16px" lineHeight={1}>
                <SoftTypography
                  sx={{
                    wordBreak: "break-word",
                    fontSize: {
                      sm: "12px",
                      lg: "14px"
                    }
                  }}
                >
                  {renderDescription()}
                </SoftTypography>
              </SoftBox>
            )}
          </SoftBox>
          {actionElement && (
            <SoftTypography
              color="secondary"
              onClick={dropdown?.action}
              sx={{
                ml: "auto",
                alignSelf: "flex-start",
                py: 1.25
              }}
            >
              {actionElement}
            </SoftTypography>
          )}
          {dropdown && (
            <SoftTypography
              color="secondary"
              onClick={dropdown?.action}
              sx={{
                ml: "auto",
                alignSelf: "flex-start",
                py: 1.25
              }}
            >
              <Icon fontSize="default" sx={{ cursor: "pointer" }}>
                more_horiz
              </Icon>
            </SoftTypography>
          )}
          {dropdown?.menu}

          {actionForRF && (
            <SoftTypography
              color="secondary"
              sx={{
                ml: "auto",
                alignSelf: "flex-start",
                py: 1.25
              }}
            >
              <FlowMenu
                item={item}
                flows={flows}
                getFlows={getFlows}
                flowUserPermissions={flowUserPermissions}
                setOpenUsersModal={setOpenUsersModal}
                setSelectedUserFlow={setSelectedUserFlow}
                actionForRF
              />
            </SoftTypography>
          )}
        </SoftBox>
        <div>
          <SoftBox
            className="browse-tiles-divider"
            sx={{
              height: "1px",
              backgroundColor: "#E0E1E2",
              marginTop: "24px",
              marginBottom: "20px"
            }}
          />
          <SoftBox display="flex" justifyContent="space-between" alignItems="center">
            {members?.length > -1 ? (
              <SoftBox display="flex" alignItems="center">
                {renderMembers}
              </SoftBox>
            ) : null}
            {dateTime ? (
              <SoftBox display="flex" flexDirection="column" lineHeight={0}>
                <SoftTypography
                  sx={{
                    fontSize: "10px",
                  }}
                >
                  <span style={{ fontWeight: 500, fontSize: "12px" }}>Last Edited:</span>
                  <span
                    style={{ display: "inline-flex", flexDirection: "column", marginLeft: "4px" }}
                  >
                    <span>{moment(dateTime).format("MM/DD/YYYY")}</span>
                    <span style={{ lineHeight: 1 }}>{moment(dateTime).format("h:mm:ss A")}</span>
                  </span>
                </SoftTypography>
              </SoftBox>
            ) : null}
          </SoftBox>
        </div>
      </SoftBox>
    </Card>
  );
}

// Typechecking props for the ProfileInfoCard
ComplexProjectCard.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
    "light"
  ]),
  image: PropTypes.string,
  title: PropTypes.string.isRequired,
  dateTime: PropTypes.string,
  description: PropTypes.node.isRequired,
  members: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.string.isRequired,
      name: PropTypes.string
    })
  ).isRequired,
  dropdown: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      action: PropTypes.func,
      menu: PropTypes.node
    })
  ]),
  status: PropTypes.string
};

export default ComplexProjectCard;
